import {
  type ClusterSort,
  ClusterStage,
  CostProvider,
  EligibilityStatus,
  EventLevel,
  ExclusionCategory,
  ExperimentStatus,
  type KeywordSort,
  OPPORTUNITY_SORT_FIELDS,
  OpportunityStatus,
  ProductType,
  ScanStatus,
  SEED_SORT_FIELDS,
  SeedType,
  YEAR_MONTH_PATTERN,
} from '@radar/contracts';
import { stripSearchParams } from '@tanstack/react-router';
import { z } from 'zod';

// Schémas zod des search params partagés. Les schémas par écran sont ajoutés par les tâches
// suivantes (`OpportunitiesSearch`, `ScansSearch`, …) à partir de ces briques.

export const PAGINATION_DEFAULTS = { page: 1, pageSize: 50 } as const;

export const PaginationSearch = z.object({
  page: z.number().int().min(1).catch(PAGINATION_DEFAULTS.page),
  pageSize: z.number().int().min(10).max(200).catch(PAGINATION_DEFAULTS.pageSize),
});

export type PaginationSearch = z.infer<typeof PaginationSearch>;

export const SortSearch = <const T extends readonly [string, ...string[]]>(
  fields: T,
  def: T[number],
  order: 'asc' | 'desc',
) =>
  z.object({
    sort: z.enum(fields).catch(def),
    order: z.enum(['asc', 'desc']).catch(order),
  });

/**
 * Middleware `stripSearchParams` typé sur la sortie du schéma : les valeurs par défaut ne sont pas
 * écrites dans l'URL (`search: { middlewares: [stripDefaults(Schema, defaults)] }`).
 */
export function stripDefaults<S extends z.ZodObject>(_schema: S, defaults: Partial<z.output<S>>) {
  return stripSearchParams<z.output<S>>(defaults);
}

/** `/seeds` (`frontend.md` § 2) : `market` est le marché courant, les tableaux sont sérialisés en JSON. */
export const SEEDS_SEARCH_DEFAULTS = {
  ...PAGINATION_DEFAULTS,
  sort: 'createdAt',
  order: 'desc',
} as const;

export const SeedsSearch = PaginationSearch.extend(
  SortSearch(SEED_SORT_FIELDS, SEEDS_SEARCH_DEFAULTS.sort, SEEDS_SEARCH_DEFAULTS.order).shape,
).extend({
  market: z.string().optional(),
  type: z.array(SeedType).optional(),
  category: z.string().optional(),
  isActive: z.boolean().optional(),
  q: z.string().optional(),
  /** Seed ouverte en édition à l'arrivée (lien depuis la page d'un scan, T35). */
  edit: z.string().optional(),
});

export type SeedsSearch = z.infer<typeof SeedsSearch>;

/** `/scans` (`frontend.md` § 2) : tri fixe `createdAt desc` côté serveur, filtres `market` et `status`. */
export const SCANS_SEARCH_DEFAULTS = { ...PAGINATION_DEFAULTS } as const;

export const ScansSearch = PaginationSearch.extend({
  market: z.string().optional(),
  status: z.array(ScanStatus).optional(),
});

export type ScansSearch = z.infer<typeof ScansSearch>;

/** `/scans/new` : seul le marché courant est dans l'URL. */
export const NewScanSearch = z.object({ market: z.string().optional() });

export type NewScanSearch = z.infer<typeof NewScanSearch>;

/** `/scans/$id` : niveaux du journal (`?level=["warn","error"]`), tous si absent. */
export const ScanDetailSearch = z.object({ level: z.array(EventLevel).optional() });

export type ScanDetailSearch = z.infer<typeof ScanDetailSearch>;
/** `/settings/exclusions` : filtres serveur (`category`, `marketId`, `isActive`), pagination client. */
export const ExclusionsSearch = PaginationSearch.extend({
  category: ExclusionCategory.optional().catch(undefined),
  marketId: z.string().optional(),
  isActive: z.boolean().optional(),
});
export type ExclusionsSearch = z.infer<typeof ExclusionsSearch>;

/**
 * `/settings/costs` (`frontend.md` § 2) : `month` en `YYYY-MM` (mois courant par défaut, absent de
 * l'URL), filtres du ledger et page du journal d'audit (`auditPage`, section repliable admin).
 */
export const COSTS_SEARCH_DEFAULTS = { ...PAGINATION_DEFAULTS, auditPage: 1 } as const;

export const CostsSearch = PaginationSearch.extend({
  month: z.string().regex(YEAR_MONTH_PATTERN).optional().catch(undefined),
  provider: CostProvider.optional().catch(undefined),
  scanId: z.string().optional(),
  auditPage: z.number().int().min(1).catch(COSTS_SEARCH_DEFAULTS.auditPage),
});
export type CostsSearch = z.infer<typeof CostsSearch>;

/** Journal d'audit (`GET /audit`) : pagination et filtres `action`, `actorId`. */
export const AuditSearch = PaginationSearch.extend({
  action: z.string().optional(),
  actorId: z.string().optional(),
});
export type AuditSearch = z.infer<typeof AuditSearch>;

/** Tris de `GET /clusters` (`ClusterSort`), en tuple pour `z.enum`. */
export const CLUSTER_SORT_FIELDS = [
  'priorityScore',
  'triageScore',
  'volumeSum',
  'updatedAt',
] as const satisfies readonly ClusterSort[];

/** `/clusters` (`frontend.md` § 2) : filtres en tableaux (JSON dans l'URL), tri `priorityScore desc`. */
export const CLUSTERS_SEARCH_DEFAULTS = {
  ...PAGINATION_DEFAULTS,
  sort: 'priorityScore',
  order: 'desc',
} as const;

export const ClustersSearch = PaginationSearch.extend(
  SortSearch(CLUSTER_SORT_FIELDS, CLUSTERS_SEARCH_DEFAULTS.sort, CLUSTERS_SEARCH_DEFAULTS.order)
    .shape,
).extend({
  market: z.string().optional(),
  stage: z.array(ClusterStage).optional(),
  eligibility: z.array(EligibilityStatus).optional(),
  minPriority: z.number().min(0).max(100).optional(),
  hasOpportunity: z.boolean().optional(),
  q: z.string().optional(),
});

export type ClustersSearch = z.infer<typeof ClustersSearch>;

export const CLUSTER_DETAIL_TABS = ['keywords', 'serp', 'analyses'] as const;
export type ClusterDetailTab = (typeof CLUSTER_DETAIL_TABS)[number];

/** Tris de l'onglet mots-clés servis par `GET /keywords?clusterId=` (`text` n'est pas proposé). */
export const CLUSTER_KEYWORD_SORTS = [
  'avgMonthlySearches',
  'highTopOfPageBid',
] as const satisfies readonly KeywordSort[];

/**
 * `/clusters/$id` (`frontend.md` § 2) : onglet, liens profonds `kw:` (`keywordId`) et `serp:`
 * (`snapshotId`, `rank`) ; `kwPage`, `kwSort`, `kwOrder` paginent et trient l'onglet mots-clés
 * côté serveur (ajout T15, absents = première page par volume décroissant). `tab` reste optionnel
 * en entrée (les liens vers la fiche n'ont pas à le porter) : absent, la page applique le lien
 * profond ou l'onglet mémorisé (`localStorage['radar.clusterTab']`), sinon `keywords`.
 */
export const ClusterDetailSearch = z.object({
  tab: z.enum(CLUSTER_DETAIL_TABS).optional().catch(undefined),
  keywordId: z.coerce.number().int().optional(),
  snapshotId: z.string().optional(),
  rank: z.coerce.number().int().optional(),
  kwPage: z.coerce.number().int().min(1).optional(),
  kwSort: z.enum(CLUSTER_KEYWORD_SORTS).optional(),
  kwOrder: z.enum(['asc', 'desc']).optional(),
});

export type ClusterDetailSearch = z.infer<typeof ClusterDetailSearch>;

/** Tris de `GET /opportunities` (`OpportunitySort`), en tuple pour `z.enum`. */
export const OPPORTUNITY_SEARCH_SORTS = OPPORTUNITY_SORT_FIELDS;

/**
 * Un tableau vide n'a pas de sens comme filtre (`status=[]` = « aucun statut ») : il est traité
 * comme absent, ce qui le retire aussi de l'URL (T23, § Notes et pièges).
 */
const filterArray = <T extends z.ZodType>(item: T) =>
  z
    .array(item)
    .transform((value) => (value.length > 0 ? value : undefined))
    .optional();

/** `/opportunities` (`frontend.md` § 2) : tri par défaut `attractiveness desc`, filtres en tableaux JSON. */
export const OPPORTUNITIES_SEARCH_DEFAULTS = {
  ...PAGINATION_DEFAULTS,
  sort: 'attractiveness',
  order: 'desc',
} as const;

export const OpportunitiesSearch = PaginationSearch.extend(
  SortSearch(
    OPPORTUNITY_SEARCH_SORTS,
    OPPORTUNITIES_SEARCH_DEFAULTS.sort,
    OPPORTUNITIES_SEARCH_DEFAULTS.order,
  ).shape,
).extend({
  market: z.string().optional(),
  status: filterArray(OpportunityStatus),
  minScore: z.number().min(0).max(100).optional().catch(undefined),
  maxScore: z.number().min(0).max(100).optional().catch(undefined),
  minConfidence: z.number().min(0).max(1).optional().catch(undefined),
  productType: filterArray(ProductType),
  /** Id d'utilisateur, ou `none` pour « non assignée » (`OWNER_NONE`). */
  ownerId: z.string().optional(),
  tags: filterArray(z.string()),
  q: z.string().optional(),
  /** Jour `YYYY-MM-DD` ou date ISO complète. */
  updatedSince: z.string().optional(),
});

export type OpportunitiesSearch = z.infer<typeof OpportunitiesSearch>;

export const OPPORTUNITY_DETAIL_TABS = [
  'overview',
  'demand',
  'supply',
  'analysis',
  'experiments',
  'activity',
] as const;
export type OpportunityDetailTab = (typeof OPPORTUNITY_DETAIL_TABS)[number];

/**
 * `/opportunities/$id` (`frontend.md` § 2) : onglet et liens profonds (`kw:` → `keywordId`,
 * `serp:<snapshotId>:<rank>` → `snapshotId` + `rank`), sélection et comparaison d'analyses.
 * `tab` est optionnel en entrée (comme `ClusterDetailSearch`, T15) : absent, la fiche applique le
 * lien profond, sinon l'onglet mémorisé (`localStorage['radar.opportunityTab']`), sinon `overview`.
 */
export const OpportunityDetailSearch = z.object({
  tab: z.enum(OPPORTUNITY_DETAIL_TABS).optional().catch(undefined),
  analysisId: z.string().optional(),
  compareId: z.string().optional(),
  snapshotId: z.string().optional(),
  rank: z.coerce.number().int().optional(),
  keywordId: z.coerce.number().int().optional(),
});

export type OpportunityDetailSearch = z.infer<typeof OpportunityDetailSearch>;
/** `/experiments` (`frontend.md` § 2) : filtres serveur, marché non partitionnant (§ 9). */
export const EXPERIMENTS_SEARCH_DEFAULTS = { ...PAGINATION_DEFAULTS } as const;

export const ExperimentsSearch = PaginationSearch.extend({
  status: z.array(ExperimentStatus).optional(),
  opportunityId: z.string().optional(),
  ownerId: z.string().optional(),
});

export type ExperimentsSearch = z.infer<typeof ExperimentsSearch>;
